// djin-cloud/web/src/components/site-detail/SiteDetailTabs.tsx
//
// Nested-route aware tab row. Alarms (`/project/:id/alarms`), Contacts
// (`/project/:id/contacts`) and Market (`/project/:id/market`) are rendered as
// child routes of ProjectSummary, so the URL drives their active highlight
// (ROUTED_TABS below). The other visible tabs (Site
// Overview, One-Line Diagram, Local Access) share the index route
// (`/project/:id`) and switch in-place via `activeInPlaceTab` /
// `onInPlaceTabChange` — these props are only consulted on the index route.
//
// Market is site-gated (djin-cloud#1089): it shows exactly when the site has a
// stored market descriptor. The gate reads the `site` prop, so every caller
// passes the site it already has (ProjectSummaryContent its own, the routed
// pages the layout's outlet context). With no site, a gated tab is hidden: it
// never flashes in before the site loads.
//
// When this component is rendered from a routed tab (SiteAlarms, SiteContacts,
// SiteMarket), clicking an in-place tab navigates to the index
// route where `activeInPlaceTab` defaults to 'site-overview'. The specific
// in-place tab target is lost on that transition — acceptable for the #149
// refactor; a URL-param-based index tab is a follow-up.
//
// Two tabs (Monthly Summary, Statistics) are currently hidden from the
// tab strip — see the commented-out entries in TABS below. Their IDs
// remain in the SiteDetailTab union and their render branches in
// ProjectSummary.tsx stay intact, so un-hiding is a one-line revert per
// tab (#148). (Live Charts was retired in #159 — superseded by the Site
// Overview Chart card.)
import type { ComponentType } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Grid3x3, Bell, ExternalLink, Share2, Users, TrendingUp } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { ROLES_WITH_LOCAL_ACCESS, type Role } from '../../constants/roles';
import { hasMarket } from '../../services/market/descriptor';
import type { SiteConfig } from '../../types/project.types';

export type SiteDetailTab = 'site-overview' | 'sld' | 'monthly-summary' | 'statistics' | 'alarms' | 'contacts' | 'market' | 'local-access';

/** Tabs that are child routes of `/project/:id`, each at `/project/:id/<id>`. */
const ROUTED_TABS = ['alarms', 'contacts', 'market'] as const;
type RoutedTab = (typeof ROUTED_TABS)[number];

interface SiteDetailTabsProps {
  siteId: string;
  /** The loaded site. Site-gated tabs (Market) are hidden without it. */
  site?: SiteConfig;
  alarmCount?: number;
  /**
   * The in-place tab active on the index route. Ignored when the URL
   * matches a routed tab (alarms, contacts, market). Optional because children
   * on routed tabs don't need it.
   */
  activeInPlaceTab?: SiteDetailTab;
  /**
   * Invoked when the user clicks an in-place tab *while already on the
   * index route*. Not used when the current URL is a routed tab — in
   * that case, in-place tabs render as <Link>s back to the index route.
   */
  onInPlaceTabChange?: (tab: SiteDetailTab) => void;
}

interface TabDef {
  id: SiteDetailTab;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** When set, the tab is only rendered if the user's role is in this list. */
  roleAllowList?: Role[];
  /** When set, the tab is only rendered for a loaded site that passes it. */
  siteGate?: (site: SiteConfig) => boolean;
}

const TABS: TabDef[] = [
  { id: 'site-overview', label: 'Site Overview', icon: Grid3x3 },
  { id: 'sld', label: 'One-Line Diagram', icon: Share2 },
  // { id: 'monthly-summary', label: 'Monthly Summary', icon: BarChart3 },   // hidden 2026-04-22 (#148) — not functional
  // { id: 'statistics', label: 'Statistics', icon: TrendingUp },            // hidden 2026-04-22 (#148) — not functional
  { id: 'alarms', label: 'Alarms', icon: Bell },
  { id: 'contacts', label: 'Contacts', icon: Users },
  { id: 'market', label: 'Market', icon: TrendingUp, siteGate: hasMarket },
  {
    id: 'local-access',
    label: 'Local Access',
    icon: ExternalLink,
    roleAllowList: ROLES_WITH_LOCAL_ACCESS, // #270 — hide from analyst + site_viewer
  },
];

const BASE_CLASS = `
  flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap
  border-b-2 transition-colors min-h-[44px] touch-manipulation
`;

function isRoutedTab(id: SiteDetailTab): id is RoutedTab {
  return (ROUTED_TABS as readonly string[]).includes(id);
}

function routedTabFromPath(pathname: string, siteId: string): RoutedTab | null {
  return ROUTED_TABS.find((tab) => pathname === `/project/${siteId}/${tab}`) ?? null;
}

export function SiteDetailTabs({
  siteId,
  site,
  alarmCount,
  activeInPlaceTab = 'site-overview',
  onInPlaceTabChange,
}: SiteDetailTabsProps) {
  const { pathname } = useLocation();
  const routedTab = routedTabFromPath(pathname, siteId);
  const onRoutedTab = routedTab !== null;
  const currentTab: SiteDetailTab = routedTab ?? activeInPlaceTab;
  const role = useAuthStore((s) => s.user?.role) as Role | undefined;

  const visibleTabs = TABS.filter(
    (t) =>
      (!t.roleAllowList || (role && t.roleAllowList.includes(role)))
      && (!t.siteGate || (site !== undefined && t.siteGate(site))),
  );

  return (
    <div className="flex items-center gap-1 px-6 border-b border-ems-border bg-ems-bg overflow-x-auto">
      {visibleTabs.map(({ id, label, icon: Icon }) => {
        const isActive = currentTab === id;
        const activeClass = isActive
          ? 'text-ems-green border-ems-green'
          : 'text-ems-text-muted border-transparent hover:text-ems-text-secondary hover:border-ems-border-bright';

        // Alarms, Contacts and Market — always routed.
        if (isRoutedTab(id)) {
          return (
            <Link
              key={id}
              to={`/project/${siteId}/${id}`}
              className={`${BASE_CLASS} ${activeClass}`}
            >
              <Icon className="w-4 h-4" />
              {label}
              {id === 'alarms' && alarmCount !== undefined && alarmCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-mono font-semibold rounded-full bg-ems-red/20 text-ems-red">
                  {alarmCount}
                </span>
              )}
            </Link>
          );
        }

        // In-place tabs. On the index route, click swaps local state via the
        // parent's onInPlaceTabChange callback. On a routed tab (Alarms /
        // Contacts / Market), local state in the sibling component isn't reachable,
        // so the click navigates back to the index route and the target
        // resets to the default ('site-overview').
        if (onRoutedTab) {
          return (
            <Link
              key={id}
              to={`/project/${siteId}`}
              className={`${BASE_CLASS} ${activeClass}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </Link>
          );
        }

        return (
          <button
            key={id}
            onClick={() => onInPlaceTabChange?.(id)}
            className={`${BASE_CLASS} ${activeClass}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        );
      })}
    </div>
  );
}
